import { useEffect, useState, useCallback } from "react";
import { useFarm } from "../context/FarmContext";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, Trash2, Printer, Scale } from "lucide-react";
import { toast } from "sonner";

function Section({ title, rows, fmt, onDelete }) {
  const total = rows.reduce((s, r) => s + r.value, 0);
  return (
    <div>
      <div className="flex items-center justify-between border-b-2 border-border pb-2 mb-2">
        <h3 className="font-display font-bold">{title}</h3>
        <span className="font-mono-nums text-sm text-muted-foreground">{fmt(total)}</span>
      </div>
      {rows.length ? rows.map((r, i) => (
        <div key={r.id || i} className="flex items-center justify-between py-1.5 text-sm group">
          <span>{r.name}</span>
          <span className="flex items-center gap-2">
            <span className="font-mono-nums">{fmt(r.value)}</span>
            {r.id && onDelete && (
              <button onClick={() => onDelete(r.id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity" data-testid={`delete-ledger-${r.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </span>
        </div>
      )) : <p className="py-2 text-xs text-muted-foreground">None recorded</p>}
    </div>
  );
}

export default function BalanceSheet() {
  const { fmt } = useFarm();
  const [bs, setBs] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", value: 0, kind: "asset", category: "current" });

  const load = useCallback(() => api.balanceSheet().then(setBs), []);
  useEffect(() => { load(); }, [load]);

  const categoriesFor = (kind) =>
    kind === "asset"
      ? [{ v: "current", l: "Current Asset" }, { v: "non_current", l: "Non-Current Asset" }]
      : [{ v: "short_term", l: "Short-term Liability" }, { v: "long_term", l: "Long-term Liability" }];

  const submit = async () => {
    if (!form.name || !form.value) return toast.error("Name and value required");
    await api.createLedger(form);
    toast.success("Entry added");
    setOpen(false);
    setForm({ name: "", value: 0, kind: "asset", category: "current" });
    load();
  };
  const remove = async (id) => { await api.deleteLedger(id); load(); toast.success("Removed"); };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Balance Sheet</h1>
          <p className="text-sm text-muted-foreground mt-1">Assets, liabilities and net farm equity.</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="h-10 rounded-full gap-1.5" data-testid="add-ledger-button"><Plus className="h-4 w-4" /> Add Entry</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle className="font-display">Add Asset / Liability</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label>Type</Label>
                  <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v, category: v === "asset" ? "current" : "short_term" })}>
                    <SelectTrigger className="mt-1.5" data-testid="ledger-kind-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="asset">Asset</SelectItem><SelectItem value="liability">Liability</SelectItem></SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="ledger-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{categoriesFor(form.kind).map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Name</Label>
                  <Input className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Land, Barn, Loan" data-testid="ledger-name-input" />
                </div>
                <div>
                  <Label>Value</Label>
                  <Input type="number" className="mt-1.5" value={form.value} onChange={(e) => setForm({ ...form, value: parseFloat(e.target.value) || 0 })} data-testid="ledger-value-input" />
                </div>
              </div>
              <DialogFooter><Button className="rounded-full" onClick={submit} data-testid="save-ledger-button">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          <Button variant="outline" className="h-10 rounded-full gap-1.5" onClick={() => window.print()} data-testid="export-pdf-button">
            <Printer className="h-4 w-4" /> Export PDF
          </Button>
        </div>
      </div>

      {bs && (
        <div className="grid lg:grid-cols-2 gap-6 print-area" data-testid="balance-sheet-table">
          <Card className="p-6 space-y-6">
            <Section title="Current Assets" rows={bs.current_assets} fmt={fmt} onDelete={remove} />
            <Section title="Non-Current Assets" rows={bs.non_current_assets} fmt={fmt} onDelete={remove} />
            <div className="flex items-center justify-between rounded-xl bg-[hsl(var(--emerald)/0.12)] text-[hsl(var(--emerald))] px-5 py-3 font-bold">
              <span className="font-display">Total Assets</span>
              <span className="font-mono-nums" data-testid="total-assets">{fmt(bs.total_assets)}</span>
            </div>
          </Card>
          <Card className="p-6 space-y-6">
            <Section title="Short-term Liabilities" rows={bs.short_term_liabilities} fmt={fmt} onDelete={remove} />
            <Section title="Long-term Liabilities" rows={bs.long_term_liabilities} fmt={fmt} onDelete={remove} />
            <div className="flex items-center justify-between rounded-xl bg-[hsl(var(--terracotta)/0.12)] text-[hsl(var(--terracotta))] px-5 py-3 font-bold">
              <span className="font-display">Total Liabilities</span>
              <span className="font-mono-nums" data-testid="total-liabilities">{fmt(bs.total_liabilities)}</span>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-primary text-primary-foreground px-5 py-4">
              <span className="font-display text-lg font-bold flex items-center gap-2"><Scale className="h-5 w-5" /> Net Equity</span>
              <span className="font-mono-nums text-2xl font-extrabold" data-testid="net-equity">{fmt(bs.net_equity)}</span>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
